/**
 * Generated by orval v8.20.0 🍺
 * Do not edit manually.
 * Iaso
 * Iaso Swagger
 * OpenAPI spec version: v1
 */
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type {
    InvalidateOptions,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from 'react-query';

import { customFetchInstance } from '../../../../../../orval/client/custom-fetch';
import { useCustomMutationOptions } from '../../../../../../orval/mutator/custom-mutation-options';
import { getCustomQueryOptions } from '../../../../../../orval/mutator/custom-query-options';
import {
    AccountCustomTranslations,
    AccountRetrieve,
    AccountRetrieveAIApiKey,
    AccountRetrieveCurrent,
    PaginatedAccountListList,
} from '../../models';
import type {
    AccountSetDefaultVersionRequest,
    AccountSwitchRequest,
    AccountUpdateAIApiKeyRequest,
    AccountUpdateRequest,
    ApiAccountsAiApiKeyDestroyParams,
    ApiAccountsAiApiKeyRetrieveParams,
    ApiAccountsAiApiKeyUpdateParams,
    ApiAccountsCustomTranslationsRetrieveParams,
    ApiAccountsListParams,
    ApiAccountsMeRetrieveParams,
    ApiAccountsPartialUpdateParams,
    ApiAccountsRetrieveParams,
    ApiAccountsSetDefaultVersionPartialUpdateParams,
    ApiAccountsSetDefaultVersionUpdateParams,
    ApiAccountsSwitchCreateParams,
    ApiAccountsUpdateParams,
    PatchedAccountSetDefaultVersionRequest,
    PatchedAccountUpdateRequest,
} from '../../models';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
    query: T,
    queryKey: K,
): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key !== 'queryKey') {
            Object.defineProperty(result, key, {
                enumerable: true,
                configurable: true,
                get: () => (query as Record<string, unknown>)[key],
            });
        }
    }
    return result;
};

export const getApiAccountsListUrl = (params?: ApiAccountsListParams) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/`;
};

export const apiAccountsList = async (
    params?: ApiAccountsListParams,
    options?: RequestInit,
): Promise<PaginatedAccountListList> => {
    return customFetchInstance<PaginatedAccountListList>(
        getApiAccountsListUrl(params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'GET',
        },
    );
};

export const getApiAccountsListQueryKey = (params?: ApiAccountsListParams) => {
    return ['apiAccountsList', ...(params ? [params] : [])] as const;
};

export const useApiAccountsListQueryOptions = <
    TData = Awaited<ReturnType<typeof apiAccountsList>>,
    TError = unknown,
>(
    params?: ApiAccountsListParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsList>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getApiAccountsListQueryKey(params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof apiAccountsList>>
    > = ({ signal }) => apiAccountsList(params, { signal, ...requestOptions });

    const customOptions = getCustomQueryOptions({
        queryKey,
        queryFn,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof apiAccountsList>>,
        TError,
        TData
    > & { queryKey: QueryKey };
};

export type ApiAccountsListQueryResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsList>>
>;
export type ApiAccountsListQueryError = unknown;

export function useApiAccountsList<
    TData = Awaited<ReturnType<typeof apiAccountsList>>,
    TError = unknown,
>(
    params?: ApiAccountsListParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsList>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
    const queryOptions = useApiAccountsListQueryOptions(params, options);

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
        queryKey: QueryKey;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const invalidateApiAccountsList = async (
    queryClient: QueryClient,
    params?: ApiAccountsListParams,
    options?: InvalidateOptions,
): Promise<QueryClient> => {
    await queryClient.invalidateQueries(
        {
            queryKey: getCustomQueryOptions({
                queryKey: getApiAccountsListQueryKey(params),
            }).queryKey,
        },
        options,
    );

    return queryClient;
};

export const getApiAccountsRetrieveUrl = (
    id: number,
    params?: ApiAccountsRetrieveParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/`;
};

export const apiAccountsRetrieve = async (
    id: number,
    params?: ApiAccountsRetrieveParams,
    options?: RequestInit,
): Promise<AccountRetrieve> => {
    return customFetchInstance<AccountRetrieve>(
        getApiAccountsRetrieveUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'GET',
        },
    );
};

export const getApiAccountsRetrieveQueryKey = (
    id: number,
    params?: ApiAccountsRetrieveParams,
) => {
    return [
        'apiAccountsRetrieve',
        ...(id ? [id] : []),
        ...(params ? [params] : []),
    ] as const;
};

export const useApiAccountsRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof apiAccountsRetrieve>>,
    TError = unknown,
>(
    id: number,
    params?: ApiAccountsRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getApiAccountsRetrieveQueryKey(id, params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof apiAccountsRetrieve>>
    > = ({ signal }) =>
        apiAccountsRetrieve(id, params, { signal, ...requestOptions });

    const customOptions = getCustomQueryOptions({
        queryKey,
        queryFn,
        enabled: id !== null && id !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof apiAccountsRetrieve>>,
        TError,
        TData
    > & { queryKey: QueryKey };
};

export type ApiAccountsRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsRetrieve>>
>;
export type ApiAccountsRetrieveQueryError = unknown;

export function useApiAccountsRetrieve<
    TData = Awaited<ReturnType<typeof apiAccountsRetrieve>>,
    TError = unknown,
>(
    id: number,
    params?: ApiAccountsRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
    const queryOptions = useApiAccountsRetrieveQueryOptions(
        id,
        params,
        options,
    );

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
        queryKey: QueryKey;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const invalidateApiAccountsRetrieve = async (
    queryClient: QueryClient,
    id: number,
    params?: ApiAccountsRetrieveParams,
    options?: InvalidateOptions,
): Promise<QueryClient> => {
    await queryClient.invalidateQueries(
        {
            queryKey: getCustomQueryOptions({
                queryKey: getApiAccountsRetrieveQueryKey(id, params),
            }).queryKey,
        },
        options,
    );

    return queryClient;
};

export const getApiAccountsUpdateUrl = (
    id: number,
    params?: ApiAccountsUpdateParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/`;
};

export const apiAccountsUpdate = async (
    id: number,
    accountUpdateRequest: AccountUpdateRequest,
    params?: ApiAccountsUpdateParams,
    options?: RequestInit,
): Promise<void> => {
    return customFetchInstance<void>(getApiAccountsUpdateUrl(id, params), {
        credentials: 'same-origin',
        ...options,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        body: JSON.stringify(accountUpdateRequest),
    });
};

export const useApiAccountsUpdateMutationOptions = <
    TError = unknown,
    TContext = unknown,
>(
    queryClient: QueryClient,
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof apiAccountsUpdate>>,
            TError,
            {
                id: number;
                data: AccountUpdateRequest;
                params?: ApiAccountsUpdateParams;
            },
            TContext
        >;
        skipInvalidation?: boolean;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseMutationOptions<
    Awaited<ReturnType<typeof apiAccountsUpdate>>,
    TError,
    {
        id: number;
        data: AccountUpdateRequest;
        params?: ApiAccountsUpdateParams;
    },
    TContext
> => {
    const mutationKey = ['apiAccountsUpdate'];
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation &&
          'mutationKey' in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof apiAccountsUpdate>>,
        {
            id: number;
            data: AccountUpdateRequest;
            params?: ApiAccountsUpdateParams;
        }
    > = props => {
        const { id, data, params } = props ?? {};

        return apiAccountsUpdate(id, data, params, requestOptions);
    };

    const onSuccess = (
        data: Awaited<ReturnType<typeof apiAccountsUpdate>>,
        variables: {
            id: number;
            data: AccountUpdateRequest;
            params?: ApiAccountsUpdateParams;
        },
        context: TContext | undefined,
    ) => {
        if (!options?.skipInvalidation) {
            queryClient.invalidateQueries({
                queryKey: getApiAccountsListQueryKey(),
            });
            queryClient.invalidateQueries({
                queryKey: getApiAccountsMeRetrieveQueryKey(),
            });
            queryClient.invalidateQueries({
                queryKey: getApiAccountsCustomTranslationsRetrieveQueryKey(
                    variables.id,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: getApiAccountsRetrieveQueryKey(variables.id),
            });
        }
        mutationOptions?.onSuccess?.(data, variables, context);
    };

    const customOptions = useCustomMutationOptions({
        ...mutationOptions,
        mutationFn,
        onSuccess,
    });

    return customOptions;
};

export type ApiAccountsUpdateMutationResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsUpdate>>
>;
export type ApiAccountsUpdateMutationBody = AccountUpdateRequest;
export type ApiAccountsUpdateMutationError = unknown;

export const useApiAccountsUpdate = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsUpdate>>,
        TError,
        {
            id: number;
            data: AccountUpdateRequest;
            params?: ApiAccountsUpdateParams;
        },
        TContext
    >;
    skipInvalidation?: boolean;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationResult<
    Awaited<ReturnType<typeof apiAccountsUpdate>>,
    TError,
    {
        id: number;
        data: AccountUpdateRequest;
        params?: ApiAccountsUpdateParams;
    },
    TContext
> => {
    const backupQueryClient = useQueryClient();
    return useMutation(
        useApiAccountsUpdateMutationOptions(backupQueryClient, options),
    );
};
export const getApiAccountsPartialUpdateUrl = (
    id: number,
    params?: ApiAccountsPartialUpdateParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/`;
};

export const apiAccountsPartialUpdate = async (
    id: number,
    patchedAccountUpdateRequest?: PatchedAccountUpdateRequest,
    params?: ApiAccountsPartialUpdateParams,
    options?: RequestInit,
): Promise<void> => {
    return customFetchInstance<void>(
        getApiAccountsPartialUpdateUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers,
            },
            body: JSON.stringify(patchedAccountUpdateRequest),
        },
    );
};

export const useApiAccountsPartialUpdateMutationOptions = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsPartialUpdate>>,
        TError,
        {
            id: number;
            data?: PatchedAccountUpdateRequest;
            params?: ApiAccountsPartialUpdateParams;
        },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationOptions<
    Awaited<ReturnType<typeof apiAccountsPartialUpdate>>,
    TError,
    {
        id: number;
        data?: PatchedAccountUpdateRequest;
        params?: ApiAccountsPartialUpdateParams;
    },
    TContext
> => {
    const mutationKey = ['apiAccountsPartialUpdate'];
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation &&
          'mutationKey' in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof apiAccountsPartialUpdate>>,
        {
            id: number;
            data?: PatchedAccountUpdateRequest;
            params?: ApiAccountsPartialUpdateParams;
        }
    > = props => {
        const { id, data, params } = props ?? {};

        return apiAccountsPartialUpdate(id, data, params, requestOptions);
    };

    const customOptions = useCustomMutationOptions({
        ...mutationOptions,
        mutationFn,
    });

    return customOptions;
};

export type ApiAccountsPartialUpdateMutationResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsPartialUpdate>>
>;
export type ApiAccountsPartialUpdateMutationBody =
    | PatchedAccountUpdateRequest
    | undefined;
export type ApiAccountsPartialUpdateMutationError = unknown;

export const useApiAccountsPartialUpdate = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsPartialUpdate>>,
        TError,
        {
            id: number;
            data?: PatchedAccountUpdateRequest;
            params?: ApiAccountsPartialUpdateParams;
        },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationResult<
    Awaited<ReturnType<typeof apiAccountsPartialUpdate>>,
    TError,
    {
        id: number;
        data?: PatchedAccountUpdateRequest;
        params?: ApiAccountsPartialUpdateParams;
    },
    TContext
> => {
    return useMutation(useApiAccountsPartialUpdateMutationOptions(options));
};
export const getApiAccountsAiApiKeyRetrieveUrl = (
    id: number,
    params?: ApiAccountsAiApiKeyRetrieveParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/ai-api-key/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/ai-api-key/`;
};

export const apiAccountsAiApiKeyRetrieve = async (
    id: number,
    params?: ApiAccountsAiApiKeyRetrieveParams,
    options?: RequestInit,
): Promise<AccountRetrieveAIApiKey> => {
    return customFetchInstance<AccountRetrieveAIApiKey>(
        getApiAccountsAiApiKeyRetrieveUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'GET',
        },
    );
};

export const getApiAccountsAiApiKeyRetrieveQueryKey = (
    id: number,
    params?: ApiAccountsAiApiKeyRetrieveParams,
) => {
    return [
        'apiAccountsAiApiKeyRetrieve',
        ...(id ? [id] : []),
        ...(params ? [params] : []),
    ] as const;
};

export const useApiAccountsAiApiKeyRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof apiAccountsAiApiKeyRetrieve>>,
    TError = unknown,
>(
    id: number,
    params?: ApiAccountsAiApiKeyRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsAiApiKeyRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getApiAccountsAiApiKeyRetrieveQueryKey(id, params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof apiAccountsAiApiKeyRetrieve>>
    > = ({ signal }) =>
        apiAccountsAiApiKeyRetrieve(id, params, { signal, ...requestOptions });

    const customOptions = getCustomQueryOptions({
        queryKey,
        queryFn,
        enabled: id !== null && id !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof apiAccountsAiApiKeyRetrieve>>,
        TError,
        TData
    > & { queryKey: QueryKey };
};

export type ApiAccountsAiApiKeyRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsAiApiKeyRetrieve>>
>;
export type ApiAccountsAiApiKeyRetrieveQueryError = unknown;

export function useApiAccountsAiApiKeyRetrieve<
    TData = Awaited<ReturnType<typeof apiAccountsAiApiKeyRetrieve>>,
    TError = unknown,
>(
    id: number,
    params?: ApiAccountsAiApiKeyRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsAiApiKeyRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
    const queryOptions = useApiAccountsAiApiKeyRetrieveQueryOptions(
        id,
        params,
        options,
    );

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
        queryKey: QueryKey;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const invalidateApiAccountsAiApiKeyRetrieve = async (
    queryClient: QueryClient,
    id: number,
    params?: ApiAccountsAiApiKeyRetrieveParams,
    options?: InvalidateOptions,
): Promise<QueryClient> => {
    await queryClient.invalidateQueries(
        {
            queryKey: getCustomQueryOptions({
                queryKey: getApiAccountsAiApiKeyRetrieveQueryKey(id, params),
            }).queryKey,
        },
        options,
    );

    return queryClient;
};

export const getApiAccountsAiApiKeyUpdateUrl = (
    id: number,
    params?: ApiAccountsAiApiKeyUpdateParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/ai-api-key/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/ai-api-key/`;
};

export const apiAccountsAiApiKeyUpdate = async (
    id: number,
    accountUpdateAIApiKeyRequest: AccountUpdateAIApiKeyRequest,
    params?: ApiAccountsAiApiKeyUpdateParams,
    options?: RequestInit,
): Promise<void> => {
    return customFetchInstance<void>(
        getApiAccountsAiApiKeyUpdateUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers,
            },
            body: JSON.stringify(accountUpdateAIApiKeyRequest),
        },
    );
};

export const useApiAccountsAiApiKeyUpdateMutationOptions = <
    TError = unknown,
    TContext = unknown,
>(
    queryClient: QueryClient,
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof apiAccountsAiApiKeyUpdate>>,
            TError,
            {
                id: number;
                data: AccountUpdateAIApiKeyRequest;
                params?: ApiAccountsAiApiKeyUpdateParams;
            },
            TContext
        >;
        skipInvalidation?: boolean;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseMutationOptions<
    Awaited<ReturnType<typeof apiAccountsAiApiKeyUpdate>>,
    TError,
    {
        id: number;
        data: AccountUpdateAIApiKeyRequest;
        params?: ApiAccountsAiApiKeyUpdateParams;
    },
    TContext
> => {
    const mutationKey = ['apiAccountsAiApiKeyUpdate'];
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation &&
          'mutationKey' in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof apiAccountsAiApiKeyUpdate>>,
        {
            id: number;
            data: AccountUpdateAIApiKeyRequest;
            params?: ApiAccountsAiApiKeyUpdateParams;
        }
    > = props => {
        const { id, data, params } = props ?? {};

        return apiAccountsAiApiKeyUpdate(id, data, params, requestOptions);
    };

    const onSuccess = (
        data: Awaited<ReturnType<typeof apiAccountsAiApiKeyUpdate>>,
        variables: {
            id: number;
            data: AccountUpdateAIApiKeyRequest;
            params?: ApiAccountsAiApiKeyUpdateParams;
        },
        context: TContext | undefined,
    ) => {
        if (!options?.skipInvalidation) {
            queryClient.invalidateQueries({
                queryKey: getApiAccountsAiApiKeyRetrieveQueryKey(variables.id),
            });
        }
        mutationOptions?.onSuccess?.(data, variables, context);
    };

    const customOptions = useCustomMutationOptions({
        ...mutationOptions,
        mutationFn,
        onSuccess,
    });

    return customOptions;
};

export type ApiAccountsAiApiKeyUpdateMutationResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsAiApiKeyUpdate>>
>;
export type ApiAccountsAiApiKeyUpdateMutationBody =
    AccountUpdateAIApiKeyRequest;
export type ApiAccountsAiApiKeyUpdateMutationError = unknown;

export const useApiAccountsAiApiKeyUpdate = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsAiApiKeyUpdate>>,
        TError,
        {
            id: number;
            data: AccountUpdateAIApiKeyRequest;
            params?: ApiAccountsAiApiKeyUpdateParams;
        },
        TContext
    >;
    skipInvalidation?: boolean;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationResult<
    Awaited<ReturnType<typeof apiAccountsAiApiKeyUpdate>>,
    TError,
    {
        id: number;
        data: AccountUpdateAIApiKeyRequest;
        params?: ApiAccountsAiApiKeyUpdateParams;
    },
    TContext
> => {
    const backupQueryClient = useQueryClient();
    return useMutation(
        useApiAccountsAiApiKeyUpdateMutationOptions(backupQueryClient, options),
    );
};
export const getApiAccountsAiApiKeyDestroyUrl = (
    id: number,
    params?: ApiAccountsAiApiKeyDestroyParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/ai-api-key/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/ai-api-key/`;
};

export const apiAccountsAiApiKeyDestroy = async (
    id: number,
    params?: ApiAccountsAiApiKeyDestroyParams,
    options?: RequestInit,
): Promise<void> => {
    return customFetchInstance<void>(
        getApiAccountsAiApiKeyDestroyUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'DELETE',
        },
    );
};

export const useApiAccountsAiApiKeyDestroyMutationOptions = <
    TError = unknown,
    TContext = unknown,
>(
    queryClient: QueryClient,
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof apiAccountsAiApiKeyDestroy>>,
            TError,
            { id: number; params?: ApiAccountsAiApiKeyDestroyParams },
            TContext
        >;
        skipInvalidation?: boolean;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseMutationOptions<
    Awaited<ReturnType<typeof apiAccountsAiApiKeyDestroy>>,
    TError,
    { id: number; params?: ApiAccountsAiApiKeyDestroyParams },
    TContext
> => {
    const mutationKey = ['apiAccountsAiApiKeyDestroy'];
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation &&
          'mutationKey' in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof apiAccountsAiApiKeyDestroy>>,
        { id: number; params?: ApiAccountsAiApiKeyDestroyParams }
    > = props => {
        const { id, params } = props ?? {};

        return apiAccountsAiApiKeyDestroy(id, params, requestOptions);
    };

    const onSuccess = (
        data: Awaited<ReturnType<typeof apiAccountsAiApiKeyDestroy>>,
        variables: { id: number; params?: ApiAccountsAiApiKeyDestroyParams },
        context: TContext | undefined,
    ) => {
        if (!options?.skipInvalidation) {
            queryClient.invalidateQueries({
                queryKey: getApiAccountsAiApiKeyRetrieveQueryKey(variables.id),
            });
        }
        mutationOptions?.onSuccess?.(data, variables, context);
    };

    const customOptions = useCustomMutationOptions({
        ...mutationOptions,
        mutationFn,
        onSuccess,
    });

    return customOptions;
};

export type ApiAccountsAiApiKeyDestroyMutationResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsAiApiKeyDestroy>>
>;

export type ApiAccountsAiApiKeyDestroyMutationError = unknown;

export const useApiAccountsAiApiKeyDestroy = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsAiApiKeyDestroy>>,
        TError,
        { id: number; params?: ApiAccountsAiApiKeyDestroyParams },
        TContext
    >;
    skipInvalidation?: boolean;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationResult<
    Awaited<ReturnType<typeof apiAccountsAiApiKeyDestroy>>,
    TError,
    { id: number; params?: ApiAccountsAiApiKeyDestroyParams },
    TContext
> => {
    const backupQueryClient = useQueryClient();
    return useMutation(
        useApiAccountsAiApiKeyDestroyMutationOptions(
            backupQueryClient,
            options,
        ),
    );
};
export const getApiAccountsCustomTranslationsRetrieveUrl = (
    id: number,
    params?: ApiAccountsCustomTranslationsRetrieveParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/custom-translations/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/custom-translations/`;
};

export const apiAccountsCustomTranslationsRetrieve = async (
    id: number,
    params?: ApiAccountsCustomTranslationsRetrieveParams,
    options?: RequestInit,
): Promise<AccountCustomTranslations> => {
    return customFetchInstance<AccountCustomTranslations>(
        getApiAccountsCustomTranslationsRetrieveUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'GET',
        },
    );
};

export const getApiAccountsCustomTranslationsRetrieveQueryKey = (
    id: number,
    params?: ApiAccountsCustomTranslationsRetrieveParams,
) => {
    return [
        'apiAccountsCustomTranslationsRetrieve',
        ...(id ? [id] : []),
        ...(params ? [params] : []),
    ] as const;
};

export const useApiAccountsCustomTranslationsRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof apiAccountsCustomTranslationsRetrieve>>,
    TError = unknown,
>(
    id: number,
    params?: ApiAccountsCustomTranslationsRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsCustomTranslationsRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getApiAccountsCustomTranslationsRetrieveQueryKey(id, params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof apiAccountsCustomTranslationsRetrieve>>
    > = ({ signal }) =>
        apiAccountsCustomTranslationsRetrieve(id, params, {
            signal,
            ...requestOptions,
        });

    const customOptions = getCustomQueryOptions({
        queryKey,
        queryFn,
        enabled: id !== null && id !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof apiAccountsCustomTranslationsRetrieve>>,
        TError,
        TData
    > & { queryKey: QueryKey };
};

export type ApiAccountsCustomTranslationsRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsCustomTranslationsRetrieve>>
>;
export type ApiAccountsCustomTranslationsRetrieveQueryError = unknown;

export function useApiAccountsCustomTranslationsRetrieve<
    TData = Awaited<ReturnType<typeof apiAccountsCustomTranslationsRetrieve>>,
    TError = unknown,
>(
    id: number,
    params?: ApiAccountsCustomTranslationsRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsCustomTranslationsRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
    const queryOptions = useApiAccountsCustomTranslationsRetrieveQueryOptions(
        id,
        params,
        options,
    );

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
        queryKey: QueryKey;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const invalidateApiAccountsCustomTranslationsRetrieve = async (
    queryClient: QueryClient,
    id: number,
    params?: ApiAccountsCustomTranslationsRetrieveParams,
    options?: InvalidateOptions,
): Promise<QueryClient> => {
    await queryClient.invalidateQueries(
        {
            queryKey: getCustomQueryOptions({
                queryKey: getApiAccountsCustomTranslationsRetrieveQueryKey(
                    id,
                    params,
                ),
            }).queryKey,
        },
        options,
    );

    return queryClient;
};

export const getApiAccountsSetDefaultVersionUpdateUrl = (
    id: number,
    params?: ApiAccountsSetDefaultVersionUpdateParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/set-default-version/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/set-default-version/`;
};

export const apiAccountsSetDefaultVersionUpdate = async (
    id: number,
    accountSetDefaultVersionRequest: AccountSetDefaultVersionRequest,
    params?: ApiAccountsSetDefaultVersionUpdateParams,
    options?: RequestInit,
): Promise<void> => {
    return customFetchInstance<void>(
        getApiAccountsSetDefaultVersionUpdateUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers,
            },
            body: JSON.stringify(accountSetDefaultVersionRequest),
        },
    );
};

export const useApiAccountsSetDefaultVersionUpdateMutationOptions = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsSetDefaultVersionUpdate>>,
        TError,
        {
            id: number;
            data: AccountSetDefaultVersionRequest;
            params?: ApiAccountsSetDefaultVersionUpdateParams;
        },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationOptions<
    Awaited<ReturnType<typeof apiAccountsSetDefaultVersionUpdate>>,
    TError,
    {
        id: number;
        data: AccountSetDefaultVersionRequest;
        params?: ApiAccountsSetDefaultVersionUpdateParams;
    },
    TContext
> => {
    const mutationKey = ['apiAccountsSetDefaultVersionUpdate'];
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation &&
          'mutationKey' in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof apiAccountsSetDefaultVersionUpdate>>,
        {
            id: number;
            data: AccountSetDefaultVersionRequest;
            params?: ApiAccountsSetDefaultVersionUpdateParams;
        }
    > = props => {
        const { id, data, params } = props ?? {};

        return apiAccountsSetDefaultVersionUpdate(
            id,
            data,
            params,
            requestOptions,
        );
    };

    const customOptions = useCustomMutationOptions({
        ...mutationOptions,
        mutationFn,
    });

    return customOptions;
};

export type ApiAccountsSetDefaultVersionUpdateMutationResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsSetDefaultVersionUpdate>>
>;
export type ApiAccountsSetDefaultVersionUpdateMutationBody =
    AccountSetDefaultVersionRequest;
export type ApiAccountsSetDefaultVersionUpdateMutationError = unknown;

export const useApiAccountsSetDefaultVersionUpdate = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsSetDefaultVersionUpdate>>,
        TError,
        {
            id: number;
            data: AccountSetDefaultVersionRequest;
            params?: ApiAccountsSetDefaultVersionUpdateParams;
        },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationResult<
    Awaited<ReturnType<typeof apiAccountsSetDefaultVersionUpdate>>,
    TError,
    {
        id: number;
        data: AccountSetDefaultVersionRequest;
        params?: ApiAccountsSetDefaultVersionUpdateParams;
    },
    TContext
> => {
    return useMutation(
        useApiAccountsSetDefaultVersionUpdateMutationOptions(options),
    );
};
export const getApiAccountsSetDefaultVersionPartialUpdateUrl = (
    id: number,
    params?: ApiAccountsSetDefaultVersionPartialUpdateParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/set-default-version/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/${id}/set-default-version/`;
};

export const apiAccountsSetDefaultVersionPartialUpdate = async (
    id: number,
    patchedAccountSetDefaultVersionRequest?: PatchedAccountSetDefaultVersionRequest,
    params?: ApiAccountsSetDefaultVersionPartialUpdateParams,
    options?: RequestInit,
): Promise<void> => {
    return customFetchInstance<void>(
        getApiAccountsSetDefaultVersionPartialUpdateUrl(id, params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers,
            },
            body: JSON.stringify(patchedAccountSetDefaultVersionRequest),
        },
    );
};

export const useApiAccountsSetDefaultVersionPartialUpdateMutationOptions = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsSetDefaultVersionPartialUpdate>>,
        TError,
        {
            id: number;
            data?: PatchedAccountSetDefaultVersionRequest;
            params?: ApiAccountsSetDefaultVersionPartialUpdateParams;
        },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationOptions<
    Awaited<ReturnType<typeof apiAccountsSetDefaultVersionPartialUpdate>>,
    TError,
    {
        id: number;
        data?: PatchedAccountSetDefaultVersionRequest;
        params?: ApiAccountsSetDefaultVersionPartialUpdateParams;
    },
    TContext
> => {
    const mutationKey = ['apiAccountsSetDefaultVersionPartialUpdate'];
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation &&
          'mutationKey' in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof apiAccountsSetDefaultVersionPartialUpdate>>,
        {
            id: number;
            data?: PatchedAccountSetDefaultVersionRequest;
            params?: ApiAccountsSetDefaultVersionPartialUpdateParams;
        }
    > = props => {
        const { id, data, params } = props ?? {};

        return apiAccountsSetDefaultVersionPartialUpdate(
            id,
            data,
            params,
            requestOptions,
        );
    };

    const customOptions = useCustomMutationOptions({
        ...mutationOptions,
        mutationFn,
    });

    return customOptions;
};

export type ApiAccountsSetDefaultVersionPartialUpdateMutationResult =
    NonNullable<
        Awaited<ReturnType<typeof apiAccountsSetDefaultVersionPartialUpdate>>
    >;
export type ApiAccountsSetDefaultVersionPartialUpdateMutationBody =
    | PatchedAccountSetDefaultVersionRequest
    | undefined;
export type ApiAccountsSetDefaultVersionPartialUpdateMutationError = unknown;

export const useApiAccountsSetDefaultVersionPartialUpdate = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsSetDefaultVersionPartialUpdate>>,
        TError,
        {
            id: number;
            data?: PatchedAccountSetDefaultVersionRequest;
            params?: ApiAccountsSetDefaultVersionPartialUpdateParams;
        },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationResult<
    Awaited<ReturnType<typeof apiAccountsSetDefaultVersionPartialUpdate>>,
    TError,
    {
        id: number;
        data?: PatchedAccountSetDefaultVersionRequest;
        params?: ApiAccountsSetDefaultVersionPartialUpdateParams;
    },
    TContext
> => {
    return useMutation(
        useApiAccountsSetDefaultVersionPartialUpdateMutationOptions(options),
    );
};
export const getApiAccountsMeRetrieveUrl = (
    params?: ApiAccountsMeRetrieveParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/me/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/me/`;
};

export const apiAccountsMeRetrieve = async (
    params?: ApiAccountsMeRetrieveParams,
    options?: RequestInit,
): Promise<AccountRetrieveCurrent> => {
    return customFetchInstance<AccountRetrieveCurrent>(
        getApiAccountsMeRetrieveUrl(params),
        {
            credentials: 'same-origin',
            ...options,
            method: 'GET',
        },
    );
};

export const getApiAccountsMeRetrieveQueryKey = (
    params?: ApiAccountsMeRetrieveParams,
) => {
    return ['apiAccountsMeRetrieve', ...(params ? [params] : [])] as const;
};

export const useApiAccountsMeRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof apiAccountsMeRetrieve>>,
    TError = unknown,
>(
    params?: ApiAccountsMeRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsMeRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getApiAccountsMeRetrieveQueryKey(params);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof apiAccountsMeRetrieve>>
    > = ({ signal }) =>
        apiAccountsMeRetrieve(params, { signal, ...requestOptions });

    const customOptions = getCustomQueryOptions({
        queryKey,
        queryFn,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof apiAccountsMeRetrieve>>,
        TError,
        TData
    > & { queryKey: QueryKey };
};

export type ApiAccountsMeRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsMeRetrieve>>
>;
export type ApiAccountsMeRetrieveQueryError = unknown;

export function useApiAccountsMeRetrieve<
    TData = Awaited<ReturnType<typeof apiAccountsMeRetrieve>>,
    TError = unknown,
>(
    params?: ApiAccountsMeRetrieveParams,
    options?: {
        query?: UseQueryOptions<
            Awaited<ReturnType<typeof apiAccountsMeRetrieve>>,
            TError,
            TData
        >;
        request?: SecondParameter<typeof customFetchInstance>;
    },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
    const queryOptions = useApiAccountsMeRetrieveQueryOptions(params, options);

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
        queryKey: QueryKey;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const invalidateApiAccountsMeRetrieve = async (
    queryClient: QueryClient,
    params?: ApiAccountsMeRetrieveParams,
    options?: InvalidateOptions,
): Promise<QueryClient> => {
    await queryClient.invalidateQueries(
        {
            queryKey: getCustomQueryOptions({
                queryKey: getApiAccountsMeRetrieveQueryKey(params),
            }).queryKey,
        },
        options,
    );

    return queryClient;
};

export const getApiAccountsSwitchCreateUrl = (
    params?: ApiAccountsSwitchCreateParams,
) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(
                key,
                value === null ? 'null' : String(value),
            );
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `${process.env.ORVAL_API_BASE_URL}/api/accounts/switch/?${stringifiedParams}`
        : `${process.env.ORVAL_API_BASE_URL}/api/accounts/switch/`;
};

export const apiAccountsSwitchCreate = async (
    accountSwitchRequest: AccountSwitchRequest,
    params?: ApiAccountsSwitchCreateParams,
    options?: RequestInit,
): Promise<void> => {
    return customFetchInstance<void>(getApiAccountsSwitchCreateUrl(params), {
        credentials: 'same-origin',
        ...options,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        body: JSON.stringify(accountSwitchRequest),
    });
};

export const useApiAccountsSwitchCreateMutationOptions = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsSwitchCreate>>,
        TError,
        { data: AccountSwitchRequest; params?: ApiAccountsSwitchCreateParams },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationOptions<
    Awaited<ReturnType<typeof apiAccountsSwitchCreate>>,
    TError,
    { data: AccountSwitchRequest; params?: ApiAccountsSwitchCreateParams },
    TContext
> => {
    const mutationKey = ['apiAccountsSwitchCreate'];
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation &&
          'mutationKey' in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof apiAccountsSwitchCreate>>,
        { data: AccountSwitchRequest; params?: ApiAccountsSwitchCreateParams }
    > = props => {
        const { data, params } = props ?? {};

        return apiAccountsSwitchCreate(data, params, requestOptions);
    };

    const customOptions = useCustomMutationOptions({
        ...mutationOptions,
        mutationFn,
    });

    return customOptions;
};

export type ApiAccountsSwitchCreateMutationResult = NonNullable<
    Awaited<ReturnType<typeof apiAccountsSwitchCreate>>
>;
export type ApiAccountsSwitchCreateMutationBody = AccountSwitchRequest;
export type ApiAccountsSwitchCreateMutationError = unknown;

export const useApiAccountsSwitchCreate = <
    TError = unknown,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof apiAccountsSwitchCreate>>,
        TError,
        { data: AccountSwitchRequest; params?: ApiAccountsSwitchCreateParams },
        TContext
    >;
    request?: SecondParameter<typeof customFetchInstance>;
}): UseMutationResult<
    Awaited<ReturnType<typeof apiAccountsSwitchCreate>>,
    TError,
    { data: AccountSwitchRequest; params?: ApiAccountsSwitchCreateParams },
    TContext
> => {
    return useMutation(useApiAccountsSwitchCreateMutationOptions(options));
};
